/**
 * Main app layout: sidebar + main content area.
 * Handles loading, channel validation, and auth state; when ready renders SideBar + Outlet.
 * Wrap routes that need this layout under a parent Route element={<MainLayout />}.
 * Uses Redux hooks (useLogin, useMetadata, useAppDispatch) instead of connect().
 */
import React, { useEffect, useRef, useState, useLayoutEffect } from "react";
import { Stack, MessageBar, MessageBarType } from "@fluentui/react";
import { Outlet } from "react-router-dom";
import { useTheme } from "../theme/OAS365Theme";
import SideBar from "../components/SideBar";
import SidebarCollapseToggle from "../components/SidebarCollapseToggle";
import MessageBox from "../components/MessageBox";
import LoginPage from "../pages/login/LoginPage";
import SuccessDialog from "../components/SuccessDialog";
import MoreDetails from "../components/MoreDetails";
import SuccessToastBox from "../components/toasts/SuccessToastBox";
import SidebarBottomFeatures from "../components/SidebarBottomFeatures";
import {
  SKIP_AUTH_DIALOG_AFTER_CANCEL,
  SKIP_AUTH_DIALOG_AFTER_LOGOUT,
} from "../services/authService";
import { useLogin } from "../redux/hooks/useLogin";
import { useAuthFlow } from "../redux/hooks/useAuthFlow";
import { useAppDispatch } from "../redux/hooks";
import { useChannel } from "../redux/hooks/useChannel";
import {
  onMainCompWidthChange,
  onWorkingDocumentListStatusChange,
} from "../redux/slices/toolsAction";
import {
  USER_DATA_LOAD_STATUS,
  TEAMS_CHANNEL_STATUS,
  DOCUMENT_LOAD_STATUS,
  PROGRESS_STATUS,
} from "../redux/constants/metaData";
import { CHANNEL_DESCRIPTION_MISSING_WARNING, GENERAL_CHANNEL_WARNING } from "../redux/constants/messageTypes";
import { getSidebarWidth } from "./sidebarLayout";

const SIDEBAR_COLLAPSED_STORAGE_KEY = "addoshare.sidebarCollapsed";

export default function MainLayout() {
  const dispatch = useAppDispatch();
  const channelStore = useChannel();
  const {
    teamsContext,
    onStartUp,
    onRetry,
  } = useLogin();
  const authFlow = useAuthFlow();
  const userDataLoadStatus = authFlow.userDataLoadStatus;
  const messageBoxInfo = authFlow.messageBoxInfo;
  const theme = useTheme();
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return sessionStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });
  const [teamsChannelStatus, setTeamsChannelStatus] = useState(
    TEAMS_CHANNEL_STATUS.CHECKING
  );
  const prevUserDataLoadStatusRef = useRef(userDataLoadStatus);
  const [connectedObjectsDismissed, setConnectedObjectsDismissed] = useState(false);
  const prevUserDataLoadStatusForBannerRef = useRef(userDataLoadStatus);

  const imgStyles = {
    maxWidth: "100%",
    maxHeight: "80%",
    // Avoid first paint at 0x0 before layout measurement is ready.
    width: width > 0 ? width : "100vw",
    height: ref.current?.clientHeight ? ref.current.clientHeight : "80vh",
    objectFit: "contain",
    objectPosition: "center",
  };

  useLayoutEffect(() => {
    if (ref.current) setWidth(ref.current.clientWidth);
  }, []);

  useLayoutEffect(() => {
    const handleResize = () => ref.current && setWidth(ref.current.clientWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    dispatch(onMainCompWidthChange(width));
  }, [width, dispatch]);

  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        sessionStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(next));
      } catch {
        /* ignore storage errors */
      }
      return next;
    });
  };

  useEffect(() => {
    // Re-show banner whenever auth state changes; logout/login may keep the same channel id.
    setConnectedObjectsDismissed(false);
  }, [userDataLoadStatus]);

  useEffect(() => {
    const checkTeamsChannel = () => {
      const isChannelIdValid =
        teamsContext?.channel?.id?.trim() !== "";
      if (isChannelIdValid) {
        setTeamsChannelStatus(TEAMS_CHANNEL_STATUS.VALID);
      } else {
        setTeamsChannelStatus(TEAMS_CHANNEL_STATUS.INVALID);
      }
    };
    checkTeamsChannel();
  }, [teamsContext]);

  useEffect(() => {
    if (teamsChannelStatus !== TEAMS_CHANNEL_STATUS.VALID) {
      if (userDataLoadStatus === USER_DATA_LOAD_STATUS.onStart) {
        console.warn(
          "[MainLayout] No API calls: channel is not valid (need a Teams channel with channel id). " +
          "Open the app from a Teams channel tab or check DevTools frame."
        );
      }
      return;
    }
    const run = async () => {
      if (userDataLoadStatus === USER_DATA_LOAD_STATUS.onStart) {
        const skipAutoAuth =
          typeof localStorage !== "undefined" &&
          (localStorage.getItem(SKIP_AUTH_DIALOG_AFTER_LOGOUT) ||
            localStorage.getItem(SKIP_AUTH_DIALOG_AFTER_CANCEL));
        if (skipAutoAuth) {
          console.info(
            "[MainLayout] Auth auto-start skipped; waiting for user Login/Retry click."
          );
          return;
        }
        console.info("[MainLayout] Starting centralized auth flow...");
        await onStartUp();
        return;
      }

      if (userDataLoadStatus === USER_DATA_LOAD_STATUS.onCompletion) {
        dispatch(onWorkingDocumentListStatusChange(DOCUMENT_LOAD_STATUS.onLoad));
      }
    };
    run();
  }, [userDataLoadStatus, teamsChannelStatus, onStartUp, dispatch]);

  // After each logout/login cycle, (re-)load channel config so we can warn when
  // Teams channel description is empty. This avoids caching issues between sessions.
  useEffect(() => {
    const prevStatus = prevUserDataLoadStatusRef.current;
    prevUserDataLoadStatusRef.current = userDataLoadStatus;

    const transitionedToCompletion =
      prevStatus !== USER_DATA_LOAD_STATUS.onCompletion &&
      userDataLoadStatus === USER_DATA_LOAD_STATUS.onCompletion;

    if (!transitionedToCompletion) {
      // Reset dismiss state while logged out / during login.
      if (userDataLoadStatus !== USER_DATA_LOAD_STATUS.onCompletion) {
        dispatch({ type: "ON_CHANNEL_CONNECTION_WARNING_RESET" });
      }
      return;
    }

    if (teamsChannelStatus !== TEAMS_CHANNEL_STATUS.VALID) return;
    if (!teamsContext?.channel?.id) return;

    dispatch({ type: "ON_CHANNEL_CONNECTION_WARNING_RESET" });
    channelStore.onChannelConfigLoad(teamsContext);
    channelStore.onCheckIsPrimaryChannel(teamsContext);
  }, [
    teamsChannelStatus,
    userDataLoadStatus,
    teamsContext,
    channelStore.onChannelConfigLoad,
    channelStore.onCheckIsPrimaryChannel,
    dispatch,
  ]);

  const showLoginPage =
    teamsChannelStatus !== TEAMS_CHANNEL_STATUS.VALID ||
    userDataLoadStatus !== USER_DATA_LOAD_STATUS.onCompletion;
  const isUnsupportedChannel =
    channelStore.isPrimaryChannel ||
    teamsContext?.channel?.membershipType === "Shared";
  const showChannelConnectionWarning =
    isUnsupportedChannel ||
    ((channelStore.channelConfigLoad === PROGRESS_STATUS.onCompletion ||
      channelStore.channelConfigLoad === PROGRESS_STATUS.noData) &&
    (!channelStore.channelObjectList || channelStore.channelObjectList.length === 0) &&
    !channelStore.channelConnectionWarningDismissed);

  const channelConnectionWarningMessage = isUnsupportedChannel
    ? GENERAL_CHANNEL_WARNING
    : CHANNEL_DESCRIPTION_MISSING_WARNING;

  const showConnectedObjectsBanner =
    channelStore.channelConfigLoad === PROGRESS_STATUS.onCompletion &&
    (channelStore.channelObjectList && channelStore.channelObjectList.length > 0) &&
    !connectedObjectsDismissed;

  const formattedObjects = (channelStore.channelObjectList || [])
    .map((obj) => {
      const type = obj.nameDesc || obj.luName || "";

      // Backend provides "docObjDesc"; when available, use it directly as the key part.
      // Otherwise, fall back to existing keyRef/keyDesc parsing logic.
      if (obj?.docObjDesc) {
        const keyVal = String(obj.docObjDesc);
        return type && keyVal ? `[${type} : ${keyVal}]` : type || keyVal;
      }

      const key = obj.keyRef || obj.keyDesc || "";

      let keyVal = key;
      if (key.includes("=") && key.includes("^")) {
        const segments = key.split("^").map((s) => s.trim()).filter(Boolean);
        const values = segments.map((seg) => {
          const parts = seg.split("=");
          return parts.length > 1 ? parts.slice(1).join("=") : seg;
        });
        keyVal = values.join(", ");
      } else {
        keyVal = key.replace(/\^/g, "");
      }

      return type && keyVal ? `[${type} : ${keyVal}]` : type || keyVal;
    })
    .filter(Boolean);

  const allObjectsText = formattedObjects.join(" , ");

  if (showLoginPage) {
    return (
      <LoginPage
        innerRef={ref}
        imgStyles={imgStyles}
        teamsChannelStatus={teamsChannelStatus}
        userDataLoadStatus={userDataLoadStatus}
        messageBoxInfo={messageBoxInfo}
        onRetry={onRetry}
      />
    );
  }

  const sidebarWidth = getSidebarWidth(sidebarCollapsed);

  return (
    <>
      <div ref={ref}>
        <Stack horizontal tokens={{ childrenGap: 0, padding: 0 }}>
          <Stack.Item
            disableShrink
            styles={{
              root: {
                position: "relative",
                width: sidebarWidth,
                minWidth: sidebarWidth,
                maxWidth: sidebarWidth,
                height: "100vh",
                overflow: "visible",
                transition: "width 0.25s ease, min-width 0.25s ease",
                borderStyle: "solid",
                borderWidth: "0px 1px 0px 0px",
                borderColor: theme.colors.Addovation20Grey,
              },
            }}
          >
            <div
              style={{
                width: sidebarWidth,
                height: "100%",
                overflow: "hidden",
                transition: "width 0.25s ease",
              }}
            >
              <SideBar collapsed={sidebarCollapsed} />
            </div>
            <SidebarCollapseToggle
              collapsed={sidebarCollapsed}
              onToggle={toggleSidebarCollapsed}
            />
          </Stack.Item>
          <Stack.Item grow style={{ height: "100vh", overflow: "hidden", display: "flex", flexDirection: "column" }}>
            {showChannelConnectionWarning && (
                <div style={{ flexShrink: 0, padding: "8px 12px 0" }}>
                  <MessageBar
                    messageBarType={MessageBarType.warning}
                    onDismiss={() =>
                      dispatch({ type: "ON_CHANNEL_CONNECTION_WARNING_DISMISS" })
                    }
                    dismissButtonAriaLabel="Dismiss"
                    styles={{
                      content: {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "100%",
                        margin: "5px",
                      },
                      text: {
                        margin: 0,
                        display: "flex",
                        alignItems: "center",
                      },
                      iconContainer: {
                        margin: 0,
                        marginRight: "8px",
                        display: "flex",
                        alignItems: "center",
                      },
                    }}
                  >
                    {channelConnectionWarningMessage}
                  </MessageBar>
                </div>
              )}
            {showConnectedObjectsBanner && (
              <div style={{ flexShrink: 0, padding: "8px 4px 0" }}>
                <MessageBar
                  messageBarType={MessageBarType.success}
                  onDismiss={() => setConnectedObjectsDismissed(true)}
                  dismissButtonAriaLabel="Dismiss"
                    styles={{
                      root: {
                        background: "#E8F5E9",
                        border: "1px solid #C8E6C9",
                        borderRadius: "10px",
                        minHeight: "40px",
                        height: "40px",
                        display: "flex",
                        alignItems: "center",
                      },
                      content: {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "100%",
                        margin: 0,
                        padding: "0 12px",
                        minHeight: "40px",
                        height: "40px",
                      },
                      text: {
                        margin: 0,
                        color: "#1B5E20",
                        fontWeight: theme.fontWeight.normal,
                        fontSize: theme.fontSizes.normal,
                        fontFamily: theme.fonts.regular,
                        minWidth: 0,
                        flexShrink: 1,
                      },
                      iconContainer: {
                        margin: 0,
                        marginLeft: "4px",
                        marginRight: "8px",
                        display: "flex",
                        alignItems: "center",
                        flexShrink: 0,
                      },
                      icon: {
                        color: "#2E7D32",
                        fontSize: "0.65em",
                        lineHeight: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: "1px",
                      },
                    }}
                >
                   <span>
                     <strong style={{ fontWeight: theme.fontWeight.titleWeight }}>
                       Connected Object(s):
                     </strong>{" "}
                     {allObjectsText}
                   </span>
                </MessageBar>
              </div>
            )}
            <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
              <Outlet />
            </div>
          </Stack.Item>
        </Stack>
      </div>
      <MessageBox />
      <SuccessToastBox />
      <MoreDetails />
      <SuccessDialog />
      <SidebarBottomFeatures dialogHost hideLinks />
    </>
  );
}
